import React, { useEffect, useState } from 'react';
import { RefreshCw, Search, UserCheck, ArrowLeft, Download, Filter } from 'lucide-react';
import http from '../api/http';
import { useFiliale } from '../context/FilialeContext';
import BlueHeader from '../components/BlueHeader';
import * as XLSX from 'xlsx';

const EmployesSortis = () => {
  const { filialeId, isConsolidated } = useFiliale();
  
  const [employes, setEmployes] = useState([]);
  const [filteredEmployes, setFilteredEmployes] = useState([]);
  const [motifsSortie, setMotifsSortie] = useState([]);
  const [departements, setDepartements] = useState([]);
  const [titres, setTitres] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showReintegrationModal, setShowReintegrationModal] = useState(false);
  const [selectedEmploye, setSelectedEmploye] = useState(null);
  
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(25);

  useEffect(() => {
    loadEmployes();
    loadMotifsSortie();
    loadDepartements();
    loadTitres();
  }, [filialeId]);

  useEffect(() => {
    // Filtrer par terme de recherche
    if (searchTerm) {
      const filtered = employes.filter(emp => 
        emp.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.prenom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.matricule?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredEmployes(filtered);
    } else {
      setFilteredEmployes(employes);
    }
    setCurrentPage(1);
  }, [searchTerm, employes]);

  const loadEmployes = async () => {
    try {
      setLoading(true);
      let endpoint = `/api/employes-sortis/sortis?date_debut=1970-01-01&date_fin=2099-12-31`;
      if (!isConsolidated && filialeId && filialeId !== 'all') {
        endpoint += `&filiale_id=${filialeId}`;
      }
      
      const response = await http.get(endpoint);
      setEmployes(response.data || []);
      setFilteredEmployes(response.data || []);
    } catch (error) {
      console.error('Erreur chargement employés:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMotifsSortie = async () => {
    try {
      const response = await http.get('/api/employes-sortis/motifs-sortie');
      setMotifsSortie(response.data || []);
    } catch (error) {
      console.error('Erreur chargement motifs:', error);
    }
  };

  const loadDepartements = async () => {
    try {
      const response = await http.get('/api/departements');
      setDepartements(response.data || []);
    } catch (error) {
      console.error('Erreur chargement départements:', error);
    }
  };

  const loadTitres = async () => {
    try {
      const response = await http.get('/api/titres');
      setTitres(response.data || []);
    } catch (error) {
      console.error('Erreur chargement titres:', error);
    }
  };

  const handleSearch = async () => {
    try {
      setLoading(true);
      let endpoint = `/api/employes-sortis/sortis?date_debut=${dateDebut || '1970-01-01'}&date_fin=${dateFin || '2099-12-31'}`;
      if (!isConsolidated && filialeId && filialeId !== 'all') {
        endpoint += `&filiale_id=${filialeId}`;
      }
      
      const response = await http.get(endpoint);
      setEmployes(response.data || []);
      setFilteredEmployes(response.data || []);
    } catch (error) {
      console.error('Erreur recherche:', error);
      alert('Erreur lors de la recherche');
    } finally {
      setLoading(false);
    }
  };

  const handleReintegration = async () => {
    if (!selectedEmploye) return;

    try {
      await http.put(`/api/employes-sortis/${selectedEmploye.idpersonnel}/reintegrer`, {
        active: 1,
        motif_sortie_id: 0,
        date_sortie: '0000-00-00'
      });
      
      setShowReintegrationModal(false);
      setSelectedEmploye(null);
      loadEmployes();
      alert('Employé réintégré avec succès');
    } catch (error) {
      console.error('Erreur réintégration:', error);
      alert('Erreur lors de la réintégration');
    }
  };

  const handleExportExcel = () => {
    const data = filteredEmployes.map(emp => ({
      'Nom': emp.nom,
      'Prénom': emp.prenom,
      'Matricule': emp.matricule,
      'Date d\'entrée': new Date(emp.date_entree).toLocaleDateString('fr-FR'),
      'Date de sortie': new Date(emp.date_sortie).toLocaleDateString('fr-FR'),
      'Motif de sortie': getMotifLibelle(emp.motif_sortie_id),
      'Département': getDepartementLibelle(emp.departement_id),
      'Poste': getTitreLibelle(emp.titre_id),
      'Type de contrat': emp.typecontrat || '-'
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Employés sortis');
    XLSX.writeFile(wb, 'employes_sortis.xlsx');
  };

  const getMotifLibelle = (motifId) => {
    const motif = motifsSortie.find(m => m.idmotif_sortie === motifId);
    return motif ? motif.libelle_motif : '';
  };

  const getDepartementLibelle = (depId) => {
    const dep = departements.find(d => d.iddepartement === depId);
    return dep ? dep.libelle_departement : '';
  };

  const getTitreLibelle = (titreId) => {
    const titre = titres.find(t => t.idtitre === titreId);
    return titre ? titre.nom : '';
  };

  const totalPages = Math.ceil(filteredEmployes.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = filteredEmployes.slice(startIndex, endIndex);

  const getStats = () => {
    const stats = {};
    motifsSortie.forEach(m => {
      stats[m.idmotif_sortie] = {
        libelle: m.libelle_motif,
        count: employes.filter(e => e.motif_sortie_id === m.idmotif_sortie).length
      };
    });
    return stats;
  };

  const stats = getStats();
  
  // S'assurer que Licenciement est toujours affiché
  const licenciementMotif = motifsSortie.find(m => m.libelle_motif.toLowerCase().includes('licenciement'));
  const topStats = Object.values(stats)
    .filter(s => s.count > 0)
    .sort((a, b) => b.count - a.count)
    .slice(0, 3);
  
  // Ajouter Licenciement s'il n'est pas déjà dans les top 3
  if (licenciementMotif && !topStats.find(s => s.libelle.toLowerCase().includes('licenciement'))) {
    topStats.push(stats[licenciementMotif.idmotif_sortie]);
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <BlueHeader
        title="Employés Sortis de l'Effectif"
        subtitle="Gérer les employés ayant quitté l'entreprise"
        actions={[
          {
            label: 'Retour',
            icon: ArrowLeft,
            onClick: () => window.history.back(),
            primary: false
          },
          {
            label: 'Actualiser',
            icon: RefreshCw,
            onClick: loadEmployes,
            primary: false
          },
          {
            label: 'Exporter Excel',
            icon: Download,
            onClick: handleExportExcel,
            primary: false
          }
        ]}
      />

      <div className="px-6 py-8 space-y-6">
        {/* Indicateurs */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="text-sm text-gray-500">Total sortis</div>
            <div className="text-2xl font-bold text-gray-900">{employes.length}</div>
          </div>
          {topStats.slice(0, 3).map((stat, idx) => (
            <div key={idx} className="bg-white rounded-xl shadow-sm p-6">
              <div className="text-sm text-gray-500">{stat.libelle}</div>
              <div className="text-2xl font-bold text-gray-900">{stat.count}</div>
            </div>
          ))}
        </div>

        {/* Filtres */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="h-5 w-5 text-gray-500" />
            <h3 className="text-lg font-semibold text-gray-800">Filtres</h3>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              {showFilters ? 'Masquer' : 'Afficher'}
            </button>
          </div>

          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date de début</label>
                <input
                  type="date"
                  value={dateDebut}
                  onChange={(e) => setDateDebut(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date de fin</label>
                <input
                  type="date"
                  value={dateFin}
                  onChange={(e) => setDateFin(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
                />
              </div>
              <div className="md:col-span-2 flex items-end gap-2">
                <button
                  onClick={handleSearch}
                  disabled={loading}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center gap-2 disabled:opacity-50"
                >
                  <Search className="h-4 w-4" />
                  {loading ? 'Recherche...' : 'Filtrer'}
                </button>
                <button
                  onClick={() => {
                    setDateDebut('');
                    setDateFin('');
                    loadEmployes();
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Réinitialiser
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Recherche */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher par nom, prénom ou matricule..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
            />
          </div>
        </div>

        {/* Tableau des employés sortis */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-800">
              Liste des employés sortis ({filteredEmployes.length})
            </h3>
          </div>

          {loading ? (
            <div className="px-6 py-12 text-center text-gray-500">
              Chargement...
            </div>
          ) : filteredEmployes.length === 0 ? (
            <div className="px-6 py-12 text-center text-gray-500">
              Aucun employé sorti trouvé
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nom & Prénom</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Matricule</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date d'entrée</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date de sortie</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Motif de sortie</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Département</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Poste</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type de contrat</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {currentData.map((employe) => (
                      <tr key={employe.idpersonnel} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900">
                            {employe.nom} {employe.prenom}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {employe.matricule || '-'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {new Date(employe.date_entree).toLocaleDateString('fr-FR')}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {new Date(employe.date_sortie).toLocaleDateString('fr-FR')}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {getMotifLibelle(employe.motif_sortie_id)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {getDepartementLibelle(employe.departement_id)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {getTitreLibelle(employe.titre_id)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {employe.typecontrat || '-'}
                        </td>
                        <td className="px-6 py-4">
                          <button
                            onClick={() => {
                              setSelectedEmploye(employe);
                              setShowReintegrationModal(true);
                            }}
                            className="px-3 py-1.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center gap-2 text-sm"
                          >
                            <UserCheck className="h-4 w-4" />
                            Réintégrer
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                  <div className="text-sm text-gray-500">
                    Affichage de {startIndex + 1} à {Math.min(endIndex, filteredEmployes.length)} sur {filteredEmployes.length}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50 hover:bg-gray-50"
                    >
                      Précédent
                    </button>
                    <span className="px-3 py-1">
                      Page {currentPage} sur {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50 hover:bg-gray-50"
                    >
                      Suivant
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Modal de réintégration */}
      {showReintegrationModal && selectedEmploye && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl border border-gray-200">
            <div className="mb-4 border-b border-gray-200 pb-4">
              <h3 className="text-lg font-bold text-gray-900">Réintégration</h3>
            </div>
            <div className="mb-6">
              <p className="text-gray-700">
                Voulez-vous vraiment réintégrer <strong>{selectedEmploye.nom} {selectedEmploye.prenom}</strong> ?
              </p>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowReintegrationModal(false);
                  setSelectedEmploye(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={handleReintegration}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                Réintégrer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployesSortis;
